import React, { useState, useEffect, useRef } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import { useAppConfig } from '../utils/useAppConfig';
import BottomNav from './BottomNav';
import { IconWallet, IconAddBalance, IconCard, IconStar, IconHistory, IconMoon, IconSun, IconLogout, IconMenu, IconChat } from './icons';

const navItems = [
  { path:'/wallet',   icon:<IconWallet size={18}/>,     label:'محفظتي' },
  { path:'/recharge', icon:<IconAddBalance size={18}/>, label:'شحن الرصيد' },
  { path:'/packages', icon:<IconCard size={18}/>,       label:'شراء كروت' },
  { path:'/loyalty',  icon:<IconStar size={18}/>,       label:'نقاطي' },
  { path:'/history',  icon:<IconHistory size={18}/>,    label:'السجل' },
];

export default function Layout({ dark, setDark }) {
  const { user, logout, refreshUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [showContact, setShowContact] = useState(false);
  const [contactMsg, setContactMsg] = useState('');
  const [sendingContact, setSendingContact] = useState(false);
  const { appLogo, appName } = useAppConfig();
  const [balanceHidden, setBalanceHidden] = useState(() => localStorage.getItem('balanceHidden') === 'true');
  const lastPingRef = useRef(0);

  const toggleBalanceVisibility = () => {
    setBalanceHidden(prev => {
      localStorage.setItem('balanceHidden', String(!prev));
      return !prev;
    });
  };

  useEffect(() => { refreshUser().catch(()=>{}); }, [location.pathname]);
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  // نبضة دورية لتحديث "آخر ظهور" طالما المستخدم نشط في التطبيق
  // (بدونها يبقى last_seen ثابتاً على وقت تسجيل الدخول فقط، فيظهر عدّاد "متصلون الآن" صفراً دائماً)
  useEffect(() => {
    if (!user) return;
    const ping = () => {
      // نتجاهل نبضة فورية لو أرسلنا وحدة قبل أقل من 5 ثواني (مثلاً تنقّلات
      // سريعة متتالية) — يمنع تكرار نفس الطلب بلا داعٍ، وما يأثر على تحديث
      // "آخر ظهور" لأن أي تنقّل حقيقي بعد كذا لسا يرسل النبضة عادي
      const now = Date.now();
      if (now - lastPingRef.current < 5000) return;
      lastPingRef.current = now;
      api.post('/auth/update-session').catch(()=>{});
    };
    ping(); // نبضة فورية عند فتح/تبديل الصفحة
    const hb = setInterval(ping, 60000); // كل دقيقة
    return () => clearInterval(hb);
  }, [user?.id, location.pathname]);

  const handleLogout = () => { logout(); navigate('/login'); };

  return (
    <div style={{ display:'flex', minHeight:'100vh', background:'var(--bg)' }}>

      {/* Mobile overlay */}
      {mobileOpen && (
        <div onClick={() => setMobileOpen(false)} style={{
          position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', zIndex:98,
          touchAction:'none'
        }}/>
      )}

      {/* ── Sidebar (desktop fixed + mobile slide) ── */}
      <aside style={{
        width:245,
        background: dark
          ? 'linear-gradient(160deg,#0d1117 0%,#161b27 100%)'
          : 'linear-gradient(160deg,var(--primary-dark) 0%,var(--primary) 100%)',
        display:'flex', flexDirection:'column',
        position:'fixed', top:0, right:0, height:'100vh',
        zIndex:100, overflow:'hidden',
        transition:'transform 0.3s cubic-bezier(.4,0,.2,1)',
        transform: mobileOpen ? 'translateX(0)' : undefined
      }} className="sidebar-nav">
        {/* Logo */}
        <div style={{ padding:'22px 18px 16px', borderBottom:'1px solid rgba(255,255,255,0.1)' }}>
          <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:13 }}>
            <div style={{ width:40, height:40, background:'var(--accent)', borderRadius:11,
              display:'flex', alignItems:'center', justifyContent:'center', fontSize:20, flexShrink:0, overflow:'hidden' }}>
              {appLogo &&
                <img src={appLogo} alt="logo" style={{ width:'100%', height:'100%', objectFit:'cover' }}/>}
            </div>
            <div>
              <div style={{ color:'white', fontWeight:900, fontSize:16 }}>{appName}</div>
              <div style={{ color:'rgba(255,255,255,0.5)', fontSize:11 }}>محفظة إنترنت</div>
            </div>
          </div>
          <div style={{ background:'rgba(255,255,255,0.1)', borderRadius:11, padding:'11px 13px' }}>
            <div style={{ color:'rgba(255,255,255,0.6)', fontSize:11, marginBottom:2 }}>مرحباً،</div>
            <div style={{ color:'white', fontWeight:700, fontSize:13, marginBottom:3 }}>📱 {user?.phone}</div>
          {user?.userId && (
            <div style={{ color:'rgba(255,255,255,0.45)', fontSize:10, fontFamily:'monospace', marginBottom:2 }}>
              🪪 {user?.userId}
            </div>
          )}
            <div style={{ display:'flex', alignItems:'baseline', gap:6 }}>
              <div style={{ color:'var(--accent)', fontWeight:900, fontSize:20, lineHeight:1 }}>
                {balanceHidden ? '••••' : Number(user?.balance||0).toFixed(2)}
                <span style={{ fontSize:12, fontWeight:500, marginRight:4, color:'rgba(255,255,255,0.7)' }}>ر</span>
              </div>
              <button onClick={toggleBalanceVisibility} style={{
                background:'none', border:'none', cursor:'pointer', padding:4,
                color:'rgba(255,255,255,0.5)', fontSize:14, lineHeight:1,
                display:'flex', alignItems:'center', justifyContent:'center'
              }} title={balanceHidden ? 'إظهار الرصيد' : 'إخفاء الرصيد'}>
                {balanceHidden ? (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>
                  </svg>
                ) : (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
                    <line x1="1" y1="1" x2="23" y2="23"/>
                  </svg>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Nav */}
        <nav style={{ flex:1, padding:'12px 10px', overflowY:'auto' }}>
          {navItems.map(item => (
            <NavLink key={item.path} to={item.path} style={({ isActive }) => ({
              display:'flex', alignItems:'center', gap:10,
              padding:'11px 13px', borderRadius:11, marginBottom:3,
              color: isActive?'white':'rgba(255,255,255,0.6)',
              background: isActive?'rgba(255,255,255,0.15)':'transparent',
              textDecoration:'none', fontWeight:isActive?700:500,
              fontSize:14, transition:'all 0.18s'
            })}>
              <span style={{ display:'flex', alignItems:'center' }}>{item.icon}</span>
              {item.label}
            </NavLink>
          ))}

          {(user?.role==='admin' || user?.role==='subadmin') && (
            <NavLink to="/admin" style={({ isActive }) => ({
              display:'flex', alignItems:'center', gap:10,
              padding:'11px 13px', borderRadius:11, marginBottom:3, marginTop:8,
              color: isActive?'#fbbf24':'rgba(251,191,36,0.7)',
              background: isActive?'rgba(251,191,36,0.15)':'transparent',
              textDecoration:'none', fontWeight:700, fontSize:14, transition:'all 0.18s',
              border:'1px dashed rgba(251,191,36,0.3)'
            })}>
              <span style={{ fontSize:18 }}>⚙️</span>لوحة الإدارة
            </NavLink>
          )}

          {/* زر الوضع الليلي — مباشرة بعد القائمة */}
          {setDark && (
            <button onClick={() => setDark(d => !d)} style={{
              width:'100%', padding:'9px 11px', borderRadius:9, border:'none', cursor:'pointer',
              fontFamily:'Tajawal', fontSize:13, fontWeight:600, textAlign:'right',
              background:'rgba(255,255,255,0.07)', color:'rgba(255,255,255,0.65)',
              display:'flex', alignItems:'center', gap:8, marginTop:8, transition:'all 0.18s'
            }}
              onMouseOver={e=>e.currentTarget.style.background='rgba(255,255,255,0.13)'}
              onMouseOut={e=>e.currentTarget.style.background='rgba(255,255,255,0.07)'}>
              {dark ? <IconSun size={15}/> : <IconMoon size={15}/>} {dark ? 'الوضع النهاري' : 'الوضع الليلي'}
            </button>
          )}

          {/* زر تسجيل الخروج — مباشرة بعد زر الوضع الليلي */}
          <button onClick={handleLogout} style={{
            width:'100%', padding:'11px 13px', borderRadius:11, border:'none',
            background:'rgba(231,76,60,0.25)', color:'#fca5a5',
            fontFamily:'Tajawal', fontSize:14, fontWeight:700,
            cursor:'pointer', display:'flex', alignItems:'center', gap:10,
            transition:'background 0.2s', marginTop:4
          }}
            onMouseOver={e=>e.currentTarget.style.background='rgba(231,76,60,0.45)'}
            onMouseOut={e=>e.currentTarget.style.background='rgba(231,76,60,0.25)'}>
            <IconLogout size={16}/> تسجيل الخروج
          </button>
        </nav>

        {/* اسم المهندس — مثبّت أسفل القائمة الجانبية دائمًا (خارج منطقة التمرير)
            حتى يبقى ظاهرًا على شاشات الجوال القصيرة دون الحاجة للتمرير لأسفل */}
        <div style={{
          flexShrink:0, padding:'10px 10px 14px',
          borderTop:'1px solid rgba(255,255,255,0.08)',
          display:'flex', alignItems:'center', justifyContent:'center', gap:6,
          direction:'rtl', flexWrap:'wrap'
        }}>
          <span style={{ color:'rgba(255,255,255,0.35)', fontSize:9.5, fontFamily:'Tajawal' }}>
            برمجة وتطوير
          </span>
          <span style={{ color:'rgba(159,225,203,0.3)', fontSize:8 }}>✦</span>
          <span style={{ color:'#9fe1cb', fontSize:11.5, fontWeight:800, fontFamily:'Tajawal' }}>
            محمد الزيلعي 770763833
          </span>
        </div>
      </aside>

      {/* ── Mobile Top Bar ── */}
      <header style={{
        display:'none', position:'fixed', top:0, left:0, right:0, height:56,
        background: dark
          ? 'linear-gradient(90deg,#0d1117,#161b27)'
          : 'linear-gradient(90deg,var(--primary-dark),var(--primary))',
        zIndex:97, alignItems:'center', justifyContent:'space-between',
        padding:'0 14px', boxShadow:'0 2px 12px rgba(0,0,0,0.2)'
      }} className="mobile-header">
        <div style={{ color:'white', fontWeight:900, fontSize:15, display:'flex', alignItems:'center', gap:8 }}>
          {appLogo &&
            <img src={appLogo} alt="logo" style={{ width:26, height:26, borderRadius:7, objectFit:'cover' }}/>} {appName}
        </div>
        <div style={{ display:'flex', gap:8, alignItems:'center' }}>
          <button onClick={() => setShowContact(true)} style={{
            background:'rgba(255,255,255,0.2)', border:'none', borderRadius:8,
            padding:'6px 10px', color:'white', fontSize:13, cursor:'pointer',
            fontFamily:'Tajawal', fontWeight:700, display:'flex', alignItems:'center', gap:4
          }}><IconChat size={15}/> مراسلة الإدارة</button>
          <button onClick={() => setMobileOpen(o=>!o)} style={{
            background:'rgba(255,255,255,0.15)', border:'none', borderRadius:8,
            padding:'6px 11px', color:'white', fontSize:20, cursor:'pointer',
            display:'flex', alignItems:'center', justifyContent:'center'
          }}><IconMenu size={18}/></button>
        </div>
      </header>

      {/* ── Main ── */}
      <main style={{ marginRight:245, flex:1, padding:'28px 22px 60px',
        minHeight:'100vh', maxWidth:'calc(100vw - 245px)' }} className="main-content">
        <Outlet />
      </main>

      {/* ── الشريط السفلي — يظهر فقط على الجوال ── */}
      <BottomNav />

      <style>{`
        @media (max-width: 768px) {
          .sidebar-nav {
            transform: translateX(${mobileOpen ? '0' : '100%'}) !important;
            width: 260px !important;
            box-shadow: -4px 0 24px rgba(0,0,0,0.2);
          }
          .mobile-header { display: flex !important; }
          .main-content {
            margin-right: 0 !important;
            max-width: 100vw !important;
            padding: 68px 14px 74px !important;
          }
        }
      `}</style>

      {/* مراسلة الإدارة Modal — متاح من أي صفحة عبر شريط الجوال العلوي */}
      {showContact && (
        <div onClick={() => setShowContact(false)} style={{
          position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', zIndex:200,
          display:'flex', alignItems:'center', justifyContent:'center', padding:20
        }}>
          <div onClick={e => e.stopPropagation()} style={{
            background:'var(--surface)', borderRadius:16, padding:22, width:'100%', maxWidth:380,
            boxShadow:'0 12px 40px rgba(0,0,0,0.3)'
          }}>
            <h3 style={{ fontWeight:900, fontSize:17, marginBottom:4, textAlign:'center' }}>💬 تواصل مع الإدارة</h3>
            <p style={{ color:'var(--text3)', fontSize:12, textAlign:'center', marginBottom:16 }}>يهمنا استفسارك واقتراحك</p>
            <textarea value={contactMsg} onChange={e => setContactMsg(e.target.value)}
              placeholder="أدخل استفسارك أو اقتراحك هنا..." rows={4}
              style={{ width:'100%', borderRadius:12, border:'1.5px solid var(--border)',
                padding:'12px 14px', fontFamily:'var(--font-display)', fontSize:14, resize:'none',
                background:'var(--surface2)', color:'var(--text)', outline:'none',
                boxSizing:'border-box', marginBottom:12 }}
              onFocus={e=>e.target.style.borderColor='var(--primary)'}
              onBlur={e=>e.target.style.borderColor='var(--border)'}/>
            <div style={{ display:'flex', gap:8 }}>
              <button onClick={() => { setShowContact(false); setContactMsg(''); }}
                style={{ flex:'0 0 80px', padding:'11px', borderRadius:10,
                  border:'1px solid var(--border)', background:'var(--surface2)',
                  fontFamily:'var(--font-display)', fontSize:13, cursor:'pointer', color:'var(--text2)' }}>إلغاء</button>
              <button disabled={sendingContact || !contactMsg.trim()}
                style={{ flex:1, padding:'11px', borderRadius:10, border:'none',
                  background:'linear-gradient(135deg,var(--primary),var(--primary-light))',
                  color:'white', fontFamily:'var(--font-display)', fontSize:14, fontWeight:800,
                  cursor:'pointer', opacity: sendingContact || !contactMsg.trim() ? 0.6 : 1 }}
                onClick={async () => {
                  setSendingContact(true);
                  try {
                    await api.post('/contact', { message: contactMsg.trim(), phone: user?.phone });
                    toast.success('✅ تم إرسال رسالتك للإدارة');
                    setContactMsg(''); setShowContact(false);
                  } catch(e) { toast.error('فشل الإرسال، حاول مرة أخرى'); }
                  finally { setSendingContact(false); }
                }}>{sendingContact ? '⏳ جاري الإرسال...' : '📤 إرسال'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
